import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus, Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { invalidateTeamAccessQueries } from '@/lib/queryInvalidation';
import { toast } from 'sonner';

interface MemberWithProfile {
  id: string;
  user_id: string;
  role: string;
  nome: string | null;
  email: string;
  avatar_url: string | null;
}

interface TeamMembersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  isAdmin: boolean;
}

export const TeamMembersModal = ({ open, onOpenChange, teamId, isAdmin }: TeamMembersModalProps) => {
  const { user } = useAuth();
  
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Fetch team members with profile info
  const { data: members, isLoading } = useQuery({
    queryKey: ['team-members', teamId],
    enabled: open && !!teamId,
    queryFn: async (): Promise<MemberWithProfile[]> => {
      const { data, error } = await supabase
        .from('team_members')
        .select('id, user_id, role, profiles(nome, email, avatar_url)')
        .eq('team_id', teamId);
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        id: row.id,
        user_id: row.user_id,
        role: row.role,
        nome: row.profiles?.nome,
        email: row.profiles?.email ?? '',
        avatar_url: row.profiles?.avatar_url,
      }));
    },
  });

  // Search profiles for adding
  const { data: searchResults } = useQuery({
    queryKey: ['profile-search', searchQuery],
    enabled: searchQuery.length >= 2,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, email, avatar_url')
        .or(`email.ilike.%${searchQuery}%,nome.ilike.%${searchQuery}%`)
        .limit(10);
      if (error) throw error;
      // Exclude existing members
      const memberIds = new Set(members?.map(m => m.user_id) ?? []);
      return (data ?? []).filter(p => !memberIds.has(p.id));
    },
  });

  const addMember = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from('team_members')
        .insert({ team_id: teamId, user_id: userId, role: 'member' });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateTeamAccessQueries(queryClient, teamId);
      setSearchQuery('');
      toast.success('Membro adicionado ao time');
    },
    onError: (err: Error) => {
      toast.error(`Erro ao adicionar membro: ${err.message}`);
    },
  });

  const updateRole = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: string }) => {
      const { error } = await supabase
        .from('team_members')
        .update({ role })
        .eq('id', memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateTeamAccessQueries(queryClient, teamId);
      toast.success('Função atualizada');
    },
    onError: (err: Error) => {
      toast.error(`Erro ao atualizar função: ${err.message}`);
    },
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateTeamAccessQueries(queryClient, teamId);
      setRemovingId(null);
      toast.success('Membro removido do time');
    },
    onError: (err: Error) => {
      setRemovingId(null);
      toast.error(`Erro ao remover membro: ${err.message}`);
    },
  });

  const roleLabels: Record<string, string> = {
    admin: 'Admin',
    member: 'Membro',
    viewer: 'Visualizador',
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle>Membros do Time</DialogTitle>
          </DialogHeader>

          {/* Add member search (admin only) */}
          {isAdmin && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Buscar por email ou nome para adicionar..."
                  className="w-full h-10 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>

              {searchResults && searchResults.length > 0 && (
                <div className="border border-color rounded-md max-h-40 overflow-y-auto">
                  {searchResults.map(profile => (
                    <button
                      key={profile.id}
                      onClick={() => addMember.mutate(profile.id)}
                      disabled={addMember.isPending}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-hover transition-colors"
                    >
                      <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                        {profile.avatar_url ? (
                          <img src={profile.avatar_url} className="w-7 h-7 rounded-full object-cover" alt="" />
                        ) : (
                          (profile.nome?.[0] ?? profile.email[0]).toUpperCase()
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-primary truncate">{profile.nome ?? 'Sem nome'}</p>
                        <p className="text-xs text-tertiary truncate">{profile.email}</p>
                      </div>
                      <UserPlus className="w-4 h-4 text-tertiary flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {searchQuery.length >= 2 && searchResults?.length === 0 && (
                <p className="text-xs text-tertiary px-1">Nenhum usuário encontrado.</p>
              )}
            </div>
          )}

          {/* Members list */}
          <div className="border border-color rounded-md divide-y divide-border max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-tertiary" />
              </div>
            ) : members && members.length > 0 ? (
              members.map(member => {
                const isSelf = member.user_id === user?.id;
                return (
                  <div key={member.id} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                      {member.avatar_url ? (
                        <img src={member.avatar_url} className="w-8 h-8 rounded-full object-cover" alt="" />
                      ) : (
                        (member.nome?.[0] ?? member.email[0]).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-primary truncate">
                        {member.nome ?? 'Sem nome'}
                        {isSelf && <span className="text-xs text-tertiary ml-1">(você)</span>}
                      </p>
                      <p className="text-xs text-tertiary truncate">{member.email}</p>
                    </div>

                    {isAdmin ? (
                      <select
                        value={member.role}
                        onChange={e => updateRole.mutate({ memberId: member.id, role: e.target.value })}
                        className="px-2 py-1 rounded border border-color bg-surface text-xs text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Membro</option>
                        <option value="viewer">Visualizador</option>
                      </select>
                    ) : (
                      <span className="text-xs text-tertiary px-2 py-1">{roleLabels[member.role] ?? member.role}</span>
                    )}

                    {isAdmin && (
                      <button
                        onClick={() => setRemovingId(member.id)}
                        className="p-1 hover:bg-hover rounded text-tertiary hover:text-destructive transition-colors"
                        title={isSelf ? 'Sair do time' : 'Remover membro'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-tertiary text-center py-8">Nenhum membro encontrado.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog open={!!removingId} onOpenChange={() => setRemovingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover membro</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este membro do time? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removingId && removeMember.mutate(removingId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
